/**
 * AuditTab — Platform audit log viewer.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollText } from 'lucide-react';
import { superAdminApi } from './superAdminApi';

export const AuditTab = () => {
  const [action, setAction] = useState('');
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['superadmin-audit', action],
    queryFn: () => superAdminApi.audit({ action: action || undefined, limit: 200 }),
    refetchInterval: 30000,
  });

  return (
    <div className="space-y-4" data-testid="superadmin-audit-tab">
      <div className="flex items-center gap-3">
        <ScrollText className="h-5 w-5 text-slate-400" />
        <Input placeholder="Filter by action (e.g. subscribe, create_tenant)..."
               value={action} onChange={e => setAction(e.target.value)}
               className="max-w-md bg-white/5 border-white/10 text-white" data-testid="audit-action-filter" />
      </div>

      <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-white/10 text-xs uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="text-left p-3">When</th>
                  <th className="text-left p-3">Actor</th>
                  <th className="text-left p-3">Action</th>
                  <th className="text-left p-3">Details</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && <tr><td colSpan={4} className="p-6 text-center text-slate-500">Loading...</td></tr>}
                {!isLoading && rows.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-slate-500">No audit events yet.</td></tr>}
                {rows.map(r => (
                  <tr key={r.id} className="border-b border-white/5">
                    <td className="p-3 text-xs text-slate-400">{r.at ? new Date(r.at).toLocaleString() : ''}</td>
                    <td className="p-3 text-xs">{r.actor_email || '—'}</td>
                    <td className="p-3">
                      <Badge variant="outline" className="capitalize text-indigo-200 border-indigo-400/30 bg-indigo-500/10">
                        {r.action?.replace(/_/g, ' ')}
                      </Badge>
                    </td>
                    <td className="p-3 text-xs text-slate-400 font-mono">
                      {r.details ? JSON.stringify(r.details) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
